/**
 * The chat sidebar's data (PLAN-20260929 slice 6): missions, hosts, the
 * host-first grouping, search, the member's profile and the attention
 * counts. Extracted unchanged from the current Sidebar so the new chat shell
 * renders the same data through design-system twins; both sidebars call it.
 */
import React from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useMissionStore } from './useMissions'
import { useConversationHistory } from './useConversationHistory'
import { useSidebarSubscriptions } from './useSidebarSubscriptions'
import { useBrowserNotifications } from './useBrowserNotifications'
import { useExecutionStore } from './useExecutionStore'
import { authService } from '../services/auth'
import { hostsApi, type ExecutionListScope, type HostResponse } from '../services/api'
import { isDraftMission, isListedMission } from '../utils/missionListing'
import type { SidebarHostGroup, SidebarMission } from '../components/sidebar/HostGroupRow'

export interface SidebarUserProfile {
  id: string
  email: string
  name: string
  role?: string
  organization_id?: string
  organizationId?: string
  organizationName?: string
  firstName?: string
  lastName?: string
}

export interface UseChatSidebarModelOptions {
  /**
   * The server scope of the run history the model reads (F-ACC-03); the new
   * chat's sidebar asks for own and invited runs. Without it the read is
   * unchanged.
   */
  historyScope?: ExecutionListScope
}

export function useChatSidebarModel(options: UseChatSidebarModelOptions = {}) {
  const { t } = useTranslation(['common', 'chat', 'errors'])
  const { missions, loadMissions, isLoading: isLoadingMissions, failure: missionsFailure } = useMissionStore()
  const missionStates = useExecutionStore((s) => s.missionStates)
  const hitlRequests = useExecutionStore((s) => s.hitlRequests)
  const governanceAlerts = useExecutionStore((s) => s.governanceAlerts)

  // Subscribe to the listed runs for sidebar events (HITL, running state),
  // under the same scope as the history read (F-ACC-03)
  useSidebarSubscriptions({ scope: options.historyScope })

  // OS notifications for HITL/governance/escalation events while tab is hidden
  useBrowserNotifications()

  // Conversation history for sidebar (grouped by mission)
  const { groupedByMission, isLoading: isLoadingConversations, refresh: refreshConversations } = useConversationHistory({
    scope: options.historyScope,
  })

  // Re-fetch conversation history when a mission starts running
  const runningMissionCount = React.useMemo(
    () => Array.from(missionStates.values()).filter(s => s.isRunning).length,
    [missionStates]
  )
  React.useEffect(() => {
    if (runningMissionCount > 0) {
      const timer = setTimeout(() => refreshConversations(), 2000)
      return () => clearTimeout(timer)
    }
  }, [runningMissionCount, refreshConversations])

  // User profile state
  const [userProfile, setUserProfile] = React.useState<SidebarUserProfile | null>(null)
  const [searchTerm, setSearchTerm] = React.useState('')

  React.useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await authService.getUserProfile()
        setUserProfile(profile)
      } catch (error) {
        console.error('Failed to load user profile:', error)
      }
    }
    loadProfile()
  }, [])

  const handleLogout = async () => {
    await authService.logout()
  }

  // Load missions on mount
  React.useEffect(() => {
    loadMissions()
  }, [loadMissions])

  // Host group names, for listing a group-bound mission under its group
  // (agion/Agion#2485). A member who cannot read them sees a generic label.
  const [hostGroupNames, setHostGroupNames] = React.useState<Map<string, string>>(new Map())
  React.useEffect(() => {
    hostsApi
      .listHostGroups()
      .then((groups) => setHostGroupNames(new Map(groups.map((g) => [g.id, g.name]))))
      .catch((error) => console.warn('Failed to fetch host groups:', error))
  }, [])

  // Fetch all hosts — used for host-first sidebar tree
  const [hosts, setHosts] = React.useState<HostResponse[]>([])
  const [isLoadingHosts, setIsLoadingHosts] = React.useState(false)

  React.useEffect(() => {
    const fetchHosts = async () => {
      setIsLoadingHosts(true)
      try {
        const response = await hostsApi.listHosts()
        setHosts(response.hosts ?? [])
      } catch (error) {
        console.warn('Failed to fetch hosts:', error)
      } finally {
        setIsLoadingHosts(false)
      }
    }

    fetchHosts()
    const interval = setInterval(fetchHosts, 30000)
    return () => clearInterval(interval)
  }, [])

  // Set of known host IDs — missions referencing unknown hosts are hidden
  const knownHostIds = React.useMemo(() => new Set(hosts.map(h => h.id)), [hosts])

  // Build the host-first group structure
  const hostGroups = React.useMemo((): SidebarHostGroup[] => {
    // 1. Build a map of missionId -> SidebarMission from missions store
    const missionMap = new Map<string, SidebarMission>()
    const missionGroupIds = new Map<string, string>()
    for (const mission of missions) {
      if (!isListedMission(mission)) continue

      const missionName = mission.name || mission.title || ''

      const hostId = mission.hostId ?? null
      // Hide missions whose host has been deleted
      if (hostId && knownHostIds.size > 0 && !knownHostIds.has(hostId)) continue

      const execState = missionStates.get(mission.id)

      missionMap.set(mission.id, {
        missionId: mission.id,
        missionName: missionName || t('chat:unnamedMission', 'Unnamed Mission'),
        isDraft: isDraftMission(mission),
        isRunning: execState?.isRunning ?? false,
        hasActiveHITL: execState?.hasActiveHITL ?? false,
        hasGovernanceAlert: execState?.hasGovernanceAlert ?? false,
        hostId,
        executions: [],
      })
      if (!hostId && mission.hostGroupId) missionGroupIds.set(mission.id, mission.hostGroupId)
    }

    // 2. Attach executions from groupedByMission to matching missions
    for (const group of groupedByMission) {
      const mission = missionMap.get(group.missionId)
      if (mission) {
        mission.executions = group.executions.map(e => ({
          executionId: e.id,
          timestamp: e.lastActivityAt,
          isActive: !!e.isActive,
        }))
      }
    }

    // 3. Seed groupMap from hosts (every host gets an entry, even empty ones)
    const groupMap = new Map<string | null, SidebarHostGroup>()
    for (const host of hosts) {
      const isOnline = host.vpn_config?.peer_connected ?? false
      groupMap.set(host.id, {
        hostId: host.id,
        hostName: host.name || host.id,
        isOnline,
        missions: [],
      })
    }

    // 4. Place each mission under its host, its host group (listed by the
    // group's name, never a single host), or unassigned
    for (const mission of missionMap.values()) {
      const groupId = mission.hostId === null ? missionGroupIds.get(mission.missionId) : undefined
      if (groupId) {
        const key = `group:${groupId}`
        let row = groupMap.get(key)
        if (!row) {
          row = {
            hostId: null,
            hostGroupId: groupId,
            hostName: hostGroupNames.get(groupId) ?? t('chat:hostGroup', 'Host group'),
            isOnline: false,
            missions: [],
          }
          groupMap.set(key, row)
        }
        row.missions.push(mission)
        continue
      }
      const hostKey = mission.hostId
      let hostGroup = groupMap.get(hostKey)
      if (!hostGroup) {
        if (hostKey === null) {
          // Unassigned group (no host)
          hostGroup = {
            hostId: null,
            hostName: t('chat:unassigned', 'Unassigned'),
            isOnline: false,
            missions: [],
          }
        } else {
          // Host not yet loaded — create a placeholder group using hostId as name
          hostGroup = {
            hostId: hostKey,
            hostName: hostKey,
            isOnline: false,
            missions: [],
          }
        }
        groupMap.set(hostKey, hostGroup)
      }
      hostGroup.missions.push(mission)
    }

    // 5. Sort missions within each host: active first, drafts last, alphabetical
    for (const group of groupMap.values()) {
      group.missions.sort((a, b) => {
        // Active missions first (governance alert > HITL > running)
        const aActive = a.hasGovernanceAlert ? 2 : (a.isRunning || a.hasActiveHITL ? 1 : 0)
        const bActive = b.hasGovernanceAlert ? 2 : (b.isRunning || b.hasActiveHITL ? 1 : 0)
        if (aActive !== bActive) return bActive - aActive
        // Drafts last
        if (a.isDraft !== b.isDraft) return a.isDraft ? 1 : -1
        // Alphabetical
        return a.missionName.localeCompare(b.missionName)
      })
    }

    // 6. Convert to array and sort: online hosts first, alphabetical, unassigned last
    const result = Array.from(groupMap.values())
    result.sort((a, b) => {
      // Unassigned always last; a host group row sorts with the hosts
      const aUnassigned = a.hostId === null && !a.hostGroupId
      const bUnassigned = b.hostId === null && !b.hostGroupId
      if (aUnassigned && !bUnassigned) return 1
      if (!aUnassigned && bUnassigned) return -1
      // Online hosts first
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1
      // Alphabetical
      return a.hostName.localeCompare(b.hostName)
    })

    // Only include unassigned group if it has missions
    return result.filter(g => g.hostId !== null || g.missions.length > 0)
  }, [missions, hosts, knownHostIds, missionStates, groupedByMission, hostGroupNames, t])

  // Filter host groups by search term
  const filteredHostGroups = React.useMemo((): SidebarHostGroup[] => {
    if (!searchTerm) return hostGroups

    const term = searchTerm.toLowerCase()
    return hostGroups
      .map(group => {
        // Host name matches → show entire group
        if (group.hostName.toLowerCase().includes(term)) return group
        // Filter missions within group
        const filteredMissions = group.missions.filter(m =>
          m.missionName.toLowerCase().includes(term)
        )
        if (filteredMissions.length === 0) return null
        return { ...group, missions: filteredMissions }
      })
      .filter((g): g is SidebarHostGroup => g !== null)
  }, [hostGroups, searchTerm])

  // Flat alphabetical list of all missions (no host grouping)
  const allMissionsFlat = React.useMemo((): SidebarMission[] => {
    const all: SidebarMission[] = []
    for (const group of filteredHostGroups) {
      all.push(...group.missions)
    }
    // Deduplicate by mission ID (a mission could appear in multiple groups)
    const seen = new Set<string>()
    const unique = all.filter(m => {
      if (seen.has(m.missionId)) return false
      seen.add(m.missionId)
      return true
    })
    // Sort alphabetically
    unique.sort((a, b) => a.missionName.localeCompare(b.missionName))
    return unique
  }, [filteredHostGroups])

  // Current mission from URL for active highlighting
  const location = useLocation()
  const urlMissionId = React.useMemo(() => {
    if (location.pathname.startsWith('/mission/')) {
      return location.pathname.split('/mission/')[1]?.split('/')[0] ?? null
    }
    return null
  }, [location.pathname])

  const isLoading = isLoadingMissions || isLoadingConversations || (isLoadingHosts && hosts.length === 0)

  return {
    userProfile,
    handleLogout,
    searchTerm,
    setSearchTerm,
    hostGroups,
    allMissionsFlat,
    urlMissionId,
    isLoading,
    missionsFailure,
    retryMissions: loadMissions,
    hitlRequests,
    governanceAlerts,
  }
}
